import type { FetchTrackedOneGraphOperation } from './ApolloNativeModule';
import type { FragmentRegistryAPI } from '@apollo/client/cache';

import { createHttpHeaderLink, typePolicies as defaultTypePolicies } from './apolloClientShared';
import { isSSR } from '../util/isSSR';

import {
    ApolloClient,
    type ApolloCache,
    type ApolloLink,
    type DefaultOptions,
    from,
    HttpLink,
    InMemoryCache,
    type NormalizedCacheObject,
    type TypePolicies,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';

const ERROR_POLICY_DEFAULTS: DefaultOptions = {
    watchQuery: { errorPolicy: 'all' },
    query: { errorPolicy: 'all' },
    mutate: { errorPolicy: 'all' },
};

function mergeDefaultOptions(overrides?: DefaultOptions): DefaultOptions {
    if (!overrides) return ERROR_POLICY_DEFAULTS;
    return {
        watchQuery: { ...ERROR_POLICY_DEFAULTS.watchQuery, ...overrides.watchQuery },
        query: { ...ERROR_POLICY_DEFAULTS.query, ...overrides.query },
        mutate: { ...ERROR_POLICY_DEFAULTS.mutate, ...overrides.mutate },
    };
}

interface CreateOneGraphApolloClientOptions {
    fetch?: FetchTrackedOneGraphOperation;
    trackingKey?: string;
    defaultOptions?: DefaultOptions;
    cache?: ApolloCache<NormalizedCacheObject>;
    platformOptions?: {
        web?: {
            graphqlUrl?: string;
            graphqlApiKey?: string;
        };
    };
    typePolicies?: TypePolicies;
    errorLink?: ApolloLink;
    prforceGroups?: string;
    fragments?: FragmentRegistryAPI;
    placementId?: string;
    country?: string;
    // SSR-only: the host's CTK, sent as the `indeed-ctk` auth header. On the
    // browser the request is credentialed via cookies instead.
    ctk?: string;
}

export function createOneGraphApolloClient({
    trackingKey,
    defaultOptions,
    cache,
    platformOptions,
    typePolicies,
    errorLink,
    prforceGroups,
    fragments,
    placementId,
    country,
    ctk,
}: CreateOneGraphApolloClientOptions): ApolloClient<NormalizedCacheObject> {
    const defaultAuthLink = setContext(({}, { headers }) => {
        return {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            headers: {
                ...headers,
                ...(isSSR()
                    ? {
                          'Indeed-Api-Key': platformOptions?.web?.graphqlApiKey,
                          'indeed-ctk': ctk,
                          'indeed-co': country,
                      }
                    : { 'indeed-api-key': platformOptions?.web?.graphqlApiKey }),
            },
        };
    });

    // SSR cannot batch (no event loop tick to coalesce on) and must rehydrate
    // nothing; the browser keeps the existing BatchHttpLink behavior.
    const httpLink = isSSR()
        ? new HttpLink({ uri: platformOptions?.web?.graphqlUrl })
        : new BatchHttpLink({
              credentials: 'include',
              uri: platformOptions?.web?.graphqlUrl,
              batchMax: 5,
              batchInterval: 5,
          });

    // On the browser, rehydrate from the cache the server serialized into the
    // page (window.__APOLLO_STATE__) so SSR'd queries don't re-run as loading.
    const resolvedCache =
        cache ??
        (() => {
            const inMemoryCache = new InMemoryCache({
                typePolicies: Object.assign({}, defaultTypePolicies, typePolicies),
                fragments,
            });
            if (!isSSR()) {
                const serializedState = (
                    window as unknown as { __APOLLO_STATE__?: NormalizedCacheObject }
                ).__APOLLO_STATE__;
                if (serializedState) {
                    inMemoryCache.restore(serializedState);
                }
            }
            return inMemoryCache;
        })();

    return new ApolloClient({
        ssrMode: isSSR(),
        cache: resolvedCache,
        defaultOptions: mergeDefaultOptions(defaultOptions),
        link: from([
            createHttpHeaderLink([
                ...(trackingKey ? [{ name: 'indeed-root-tracking-key', value: trackingKey }] : []),
                ...(prforceGroups ? [{ name: 'indeed-prforce-groups', value: prforceGroups }] : []),
                ...(placementId ? [{ name: 'indeed-placementid', value: placementId }] : []),
            ]),
            ...(errorLink ? [errorLink] : []),
            defaultAuthLink.concat(httpLink),
        ]),
    });
}
